"use client";

import React from "react";
import { ArrowRight, Cog, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function Comparison() {
  const shouldReduceMotion = useReducedMotion();
  const animationProps = shouldReduceMotion ? {
    initial: {
      opacity: 1,
      y: 0
    },
    animate: {
      opacity: 1,
      y: 0
    }
  } : {
    initial: {
      opacity: 0,
      y: 12
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.3,
      ease: [0.16, 1, 0.3, 1]
    }
  };
  return <section id="usedvsnewcomparison" data-nav="light" className="border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <motion.div {...animationProps} className="flex flex-col items-center">
          {/* Section Header Block */}
          <div className="mx-auto mb-12 max-w-3xl text-center md:mb-16">
            <h2 className="font-serif text-3xl font-bold tracking-tight uppercase sm:text-4xl md:text-5xl">
              Used vs New Mechanical Bulls
            </h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
              Used equipment can reduce the initial purchase cost, while new equipment offers a known starting condition. Neither option is automatically better.
            </p>
          </div>

          {/* Two-Column Comparison Cards */}
          <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            {/* Left Card: Used */}
            <Card className="flex flex-col justify-between rounded-none border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-foreground/30">
              <div>
                <CardHeader className="border-b border-border p-6 md:p-8">
                  <div className="mb-4 flex items-center justify-end">
                    <div className="flex size-8 items-center justify-center border border-border bg-muted">
                      <Cog className="size-4 text-foreground" />
                    </div>
                  </div>
                  <CardTitle className="font-serif text-2xl font-bold uppercase tracking-tight text-foreground md:text-3xl">
                    Consider Used When
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 md:p-8">
                  <p className="text-base leading-relaxed text-muted-foreground">
                    You want to reduce the upfront purchase cost and the available condition information gives you enough confidence to evaluate the exact machine.
                  </p>
                </CardContent>
              </div>
            </Card>

            {/* Right Card: New */}
            <Card className="flex flex-col justify-between rounded-none border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-foreground/30">
              <div>
                <CardHeader className="border-b border-border p-6 md:p-8">
                  <div className="mb-4 flex items-center justify-end">
                    <div className="flex size-8 items-center justify-center border border-border bg-muted">
                      <ShieldCheck className="size-4 text-foreground" />
                    </div>
                  </div>
                  <CardTitle className="font-serif text-2xl font-bold uppercase tracking-tight text-foreground md:text-3xl">
                    Consider New When
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 md:p-8">
                  <p className="text-base leading-relaxed text-muted-foreground">
                    You prefer a known starting condition, current product information and whatever warranty or manufacturer support applies to the specific model.
                  </p>
                </CardContent>
              </div>
            </Card>
          </div>

          {/* Bottom Grounded Summary Callout */}
          <div className="mt-10 w-full border border-border bg-card p-6 md:mt-12 md:p-8">
            <div className="flex flex-col items-center justify-between gap-6 lg:flex-row">
              <div className="max-w-2xl text-center lg:text-left">
                <p className="font-serif text-xl font-bold uppercase tracking-tight text-foreground sm:text-2xl">
                  The best comparison is between actual machines, not simply between the words new and used.
                </p>
              </div>

              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                <Button asChild className="rounded-none bg-primary px-6 py-5 text-sm font-semibold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-primary/90">
                  <Link to="#">
                    <span>View Used Bulls</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="rounded-none border-secondary bg-secondary px-6 py-5 text-sm font-semibold uppercase tracking-wider text-secondary-foreground transition-colors hover:bg-secondary/80">
                  <Link to="/NewMechanicalBullsPage">
                    <span>Shop New Bulls</span>
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}